import { ParsedCSVData, ValidationError } from '@/types/upload';
import { MenuBlockViewTypeSchema, MenuItemViewTypeSchema } from '@/types/api.zod';
import { type CsvRow, toCsvRow } from '@/utils/csvRow';

/**
 * 検索メニュー CSV のクライアント側バリデーション。
 *
 * S3 へ presigned URL でアップロードする前に Book / Product と同様、全ルールを
 * クライアントで検証してプレビュー可否を判定する。サーバー（ProcessUpload Lambda /
 * MenuUploadService）でも同一ルールを再検証する（多層防御）。
 *
 * 仕様の正本: docs/features/menu.md「CSV フォーマット」「CSV バリデーションルール」。
 * view_type の許容値は codegen の Zod enum（単一の出所）から導出する。
 *
 * 権威はサーバー（MenuUploadService）側にあり、ここはアップロード前の UX 向け事前検証。
 * menu_id / platform / 日時のルール（下記の正規表現等）はバックエンドと手動ミラーのため、
 * 仕様変更時は docs/features/menu.md を基点に menu_service.py と本ファイルの両方を更新すること。
 */

const COLUMNS = [
  'data_type',
  'menu_id',
  'start_at',
  'stop_at',
  'title',
  'link_to',
  'link_to_target',
  'view_type',
  'platform',
] as const;

// 列名をキーに持つ 1 行分の型（値は常に string）。詳細は utils/csvRow.ts。
type MenuRow = CsvRow<typeof COLUMNS>;

const DEFAULT_MENU_ID = 'default';
const MENU_VIEW_TYPE = 'hidden';
const MENU_ID_RE = /^[a-zA-Z0-9_-]{1,20}$/;
const PLATFORM_RE = /^[01]{3}$/;
// title（BLOCK 見出し / ITEM 表示名）の最大文字数。schemas/components/entities/menu.yaml
// の maxLength と一致させること。文字数はサーバー（Pydantic）に合わせコードポイントで数える。
const TITLE_MAX_LENGTH = 25;

// 受理する日時形式（区切り / または -、時刻・秒は任意）。サーバー側
// DateTimeUtils.csv_date_to_iso と同じパターン。DB 保存時に JST 付き ISO8601 へ正規化される。
const CSV_DATETIME_RE = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/;

const DATETIME_FORMAT_HINT = '（例: 2026-06-01 / 2026/06/01 00:00 / 2026-06-01 00:00:00）';

// 型注釈で受けることでキャストが不要になる（AGENTS.md 1: キャスト禁止）。
// 値の照合は「CSV の任意文字列が候補に含まれるか」なので、比較用に広い型で扱う。
const BLOCK_VIEW_TYPES: readonly string[] = MenuBlockViewTypeSchema.options;
const ITEM_VIEW_TYPES: readonly string[] = MenuItemViewTypeSchema.options;

// CSV のタイムゾーン無し日付は JST（UTC+9）とみなす（サーバ DateTimeUtils.csv_date_to_iso と一致）。
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

/**
 * 柔軟な日時パース（比較用に epoch ミリ秒を返す。不正なら null）。
 * - CSV 形式（/ または -、時刻・秒省略可）→ TZ 無しは JST として解釈
 * - タイムゾーン付き ISO8601（+09:00 / Z）→ 絶対時刻
 *
 * CSV 形式を JST 固定で解釈することで、ブラウザのローカル TZ に依存せず、
 * かつ +09:00 付き ISO8601 との `stop_at < start_at` 比較が TZ 混在で誤判定しない
 * （サーバ側の正規化・比較と同一基準）。
 */
const parseFlexibleDateTime = (value: string): number | null => {
  const s = value.trim();
  const m = CSV_DATETIME_RE.exec(s);
  if (m) {
    const [, y, mo, d, h, mi, sec] = m;
    const year = Number(y);
    const month = Number(mo);
    const day = Number(d);
    const hour = Number(h ?? 0);
    const minute = Number(mi ?? 0);
    const second = Number(sec ?? 0);
    // 月日のロールオーバー（例: 2026-13-01）を UTC ベースで判定して弾く
    const probe = new Date(Date.UTC(year, month - 1, day));
    if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) {
      return null;
    }
    // 時刻の範囲チェック（例: 25:70:99 を弾く。Date.UTC は翌日へロールオーバーして
    // 受理してしまうため明示的に検証する。Corner / Special の isValidDateTime と同一方式で、
    // サーバの strptime が弾く範囲と一致させる）。
    if (hour > 23 || minute > 59 || second > 59) return null;
    // 壁時計時刻を JST として絶対時刻（epoch ミリ秒）へ。ローカル TZ 非依存。
    return Date.UTC(year, month - 1, day, hour, minute, second) - JST_OFFSET_MS;
  }
  const t = Date.parse(s); // ISO8601（offset / Z）→ 絶対時刻
  return Number.isNaN(t) ? null : t;
};

const isValidDateTime = (value: string): boolean => parseFlexibleDateTime(value) !== null;

const err = (row: number, column: string, value: string, error: string): ValidationError => ({
  row,
  column,
  value,
  error,
});

const validateTitle = (
  row: number,
  value: string,
  kind: string,
  errors: ValidationError[],
): void => {
  if (value && [...value].length > TITLE_MAX_LENGTH) {
    errors.push(err(row, 'title', value, `${kind} の title は ${TITLE_MAX_LENGTH} 文字以内で指定してください`));
  }
};

const validatePlatform = (
  row: number,
  value: string,
  kind: string,
  errors: ValidationError[],
): void => {
  if (!value) {
    errors.push(err(row, 'platform', value, `${kind} の platform は必須です`));
  } else if (!PLATFORM_RE.test(value)) {
    errors.push(err(row, 'platform', value, 'platform は 3 桁（web/ios/android, 0/1）で指定してください'));
  } else if (value === '000') {
    errors.push(err(row, 'platform', value, 'platform は 000 不可（最低 1 つは表示）'));
  }
};

const validateMenuRow = (row: number, d: MenuRow, errors: ValidationError[]): void => {
  const menuId = d.menu_id;
  if (!menuId) {
    errors.push(err(row, 'menu_id', menuId, 'menu_id は必須です'));
  } else if (!MENU_ID_RE.test(menuId)) {
    errors.push(err(row, 'menu_id', menuId, 'menu_id は英数字・- _ のみ・20 文字以内です'));
  }

  const startAt = d.start_at;
  if (!startAt) {
    errors.push(err(row, 'start_at', startAt, 'start_at は必須です'));
  } else if (!isValidDateTime(startAt)) {
    errors.push(err(row, 'start_at', startAt, `start_at の日時形式が不正です${DATETIME_FORMAT_HINT}`));
  }

  const stopAt = d.stop_at;
  if (!menuId) {
    // menu_id 必須エラーは上で追加済み。menu_id 未確定では default 判定ができず
    // 「default 以外は stop_at 必須」が誤解を招くため stop_at の要否判定はスキップする。
  } else if (menuId === DEFAULT_MENU_ID) {
    if (stopAt) {
      errors.push(err(row, 'stop_at', stopAt, 'default は stop_at を指定できません（無期限）'));
    }
  } else if (!stopAt) {
    errors.push(err(row, 'stop_at', stopAt, 'default 以外は stop_at が必須です'));
  } else if (!isValidDateTime(stopAt)) {
    errors.push(err(row, 'stop_at', stopAt, `stop_at の日時形式が不正です${DATETIME_FORMAT_HINT}`));
  }

  const startMs = startAt ? parseFlexibleDateTime(startAt) : null;
  const stopMs = stopAt ? parseFlexibleDateTime(stopAt) : null;
  if (startMs !== null && stopMs !== null && stopMs < startMs) {
    errors.push(err(row, 'stop_at', stopAt, 'stop_at は start_at より前の時刻を指定できません'));
  }

  if (d.view_type !== MENU_VIEW_TYPE) {
    errors.push(err(row, 'view_type', d.view_type, `MENU の view_type は ${MENU_VIEW_TYPE} のみ可`));
  }

  (['platform', 'link_to', 'link_to_target'] as const).forEach((f) => {
    if (d[f]) errors.push(err(row, f, d[f], `MENU 行に ${f} は指定できません`));
  });
};

const validateBlockRow = (row: number, d: MenuRow, errors: ValidationError[]): void => {
  if (!BLOCK_VIEW_TYPES.includes(d.view_type)) {
    errors.push(
      err(row, 'view_type', d.view_type, `BLOCK の view_type は ${BLOCK_VIEW_TYPES.join(' / ')} のいずれか（double 不可）`),
    );
  }
  validateTitle(row, d.title, 'BLOCK', errors);
  validatePlatform(row, d.platform, 'BLOCK', errors);
  (['menu_id', 'start_at', 'stop_at', 'link_to', 'link_to_target'] as const).forEach((f) => {
    if (d[f]) errors.push(err(row, f, d[f], `BLOCK 行に ${f} は指定できません`));
  });
};

const validateItemRow = (row: number, d: MenuRow, errors: ValidationError[]): void => {
  if (!ITEM_VIEW_TYPES.includes(d.view_type)) {
    errors.push(err(row, 'view_type', d.view_type, `ITEM の view_type は ${ITEM_VIEW_TYPES.join(' / ')} のいずれか`));
  }
  if (!d.link_to) {
    errors.push(err(row, 'link_to', d.link_to, 'ITEM の link_to は必須です'));
  }
  validateTitle(row, d.title, 'ITEM', errors);
  validatePlatform(row, d.platform, 'ITEM', errors);
  (['menu_id', 'start_at', 'stop_at'] as const).forEach((f) => {
    if (d[f]) errors.push(err(row, f, d[f], `ITEM 行に ${f} は指定できません`));
  });
};

export const validateMenuCsv = (data: ParsedCSVData): ValidationError[] => {
  const errors: ValidationError[] = [];

  // ヘッダー（順序込み完全一致）
  const headerMatches =
    data.headers.length === COLUMNS.length && COLUMNS.every((c, i) => data.headers[i] === c);
  if (!headerMatches) {
    return [err(1, 'ヘッダー', data.headers.join(','), `CSVヘッダーが不正です。期待: ${COLUMNS.join(',')}`)];
  }

  if (data.rows.length === 0) {
    return [err(1, 'データ', '', 'データ行がありません（1=定義名 / 2=表示名 / 3 行目以降=データ）')];
  }

  let menuCount = 0;
  let blockCount = 0;
  let currentBlockHasItem = false;
  let currentBlockRow = 0;
  let sawBlock = false;

  // Book / Product と同じ 3 行構成（1=定義名 / 2=表示名 / 3 行目以降=データ）。
  // parseCSV が 2 行目をスキップ済みのため data.rows[0] は実ファイル 3 行目に相当する。
  data.rows.forEach((row, idx) => {
    const rowNum = idx + 3;

    if (row.length !== COLUMNS.length) {
      errors.push(err(rowNum, '列数', row.join(','), `列数が不正です（${row.length} 列、期待 ${COLUMNS.length} 列）`));
      return;
    }

    const d = toCsvRow(COLUMNS, row);

    switch (d.data_type) {
      case 'MENU':
        menuCount += 1;
        validateMenuRow(rowNum, d, errors);
        break;
      case 'BLOCK':
        if (sawBlock && !currentBlockHasItem) {
          errors.push(err(currentBlockRow, 'data_type', 'BLOCK', 'BLOCK に ITEM がありません（最低 1 アイテム必須）'));
        }
        sawBlock = true;
        blockCount += 1;
        currentBlockHasItem = false;
        currentBlockRow = rowNum;
        validateBlockRow(rowNum, d, errors);
        break;
      case 'ITEM':
        validateItemRow(rowNum, d, errors);
        if (!sawBlock) {
          errors.push(err(rowNum, 'data_type', 'ITEM', 'ITEM 行が BLOCK より前に存在します'));
        } else {
          currentBlockHasItem = true;
        }
        break;
      default:
        errors.push(
          err(rowNum, 'data_type', d.data_type, `data_type が不正です（MENU / BLOCK / ITEM のみ）`),
        );
    }
  });

  // 最後の BLOCK に ITEM が無い場合
  if (sawBlock && !currentBlockHasItem) {
    errors.push(err(currentBlockRow, 'data_type', 'BLOCK', 'BLOCK に ITEM がありません（最低 1 アイテム必須）'));
  }

  // 構造（行跨ぎ）
  if (menuCount === 0) {
    errors.push(err(1, 'data_type', '', 'MENU 行がありません（CSV につき 1 行必須）'));
  } else if (menuCount > 1) {
    errors.push(err(1, 'data_type', 'MENU', 'MENU 行は 1 行のみ可'));
  }
  if (blockCount === 0) {
    errors.push(err(1, 'data_type', '', 'BLOCK 行がありません（最低 1 ブロック必須）'));
  }

  return errors;
};
